<template>
  <div class>
    <v-toolbar dark :color="$gf.headerColor()">
      <v-toolbar-title>
        <v-icon class="icon">fa-plus</v-icon>Add Company
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn fab small class="close" @click="closePanel">
        <v-icon dark>fa-times</v-icon>
      </v-btn>
    </v-toolbar>
    <v-container>
      <v-layout>
        <v-flex xs12>
          <v-form @submit.prevent="addCompany">
            <v-text-field
              :error-messages="messages.company_name"
              v-model="user.company_name"
              label="COMPANY NAME"
              type="text"
              filled
              counter="50"
            ></v-text-field>
            <v-text-field
              :error-messages="messages.first_name"
              v-model="user.first_name"
              label="OWNER FIRST NAME"
              type="text"
              filled
            ></v-text-field>
            <v-text-field
              :error-messages="messages.last_name"
              v-model="user.last_name"
              label="OWNER LAST NAME"
              type="text"
              filled
            ></v-text-field>
            <v-text-field
              :error-messages="messages.email"
              v-model="user.email"
              label="WORK EMAIL"
              type="text"
              filled
              hint="A business email address. Personal providers such as gmail, yahoo, hotmail and outlook are rejected."
              persistent-hint
            ></v-text-field>
            <v-select
              :error-messages="messages.banding"
              v-model="user.banding"
              :items="bandingOptions"
              :loading="bandingLoading"
              :disabled="bandingLoading || bandingFailed"
              item-value="id"
              item-text="name"
              label="BANDING"
              filled
              attach
              return-object
              class="mt-4"
            ></v-select>
            <v-alert v-if="bandingFailed" type="warning" dense outlined class="mb-4">
              {{ bandingFailedMessage }}
            </v-alert>
            <v-text-field
              :error-messages="messages.phone"
              v-model="user.phone"
              label="PHONE (OPTIONAL)"
              type="text"
              filled
            ></v-text-field>
            <p class="caption mb-0">
              No password is set here. The owner receives a welcome email with a link to set their
              own password.
            </p>
          </v-form>
        </v-flex>
      </v-layout>
    </v-container>
    <v-footer absolute height="68">
      <v-flex px-4 py-4 text-right>
        <v-btn color="primary" class="mr-1" @click="closePanel" :disabled="loading">Cancel</v-btn>
        <v-btn v-if="loading" color="primary" loading></v-btn>
        <v-btn v-else color="primary" @click="addCompany">Add Company</v-btn>
      </v-flex>
    </v-footer>
  </div>
</template>
<script>
import { EventBus } from '@/event-bus.js';
import userAPI from '@/api/user.js';
import companyAPI from '@/api/company.js';

const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGIT = '23456789';
const SPECIAL = '?@#$%^&+=!*()-';

export default {
  name: 'CreateDirectCompany',
  props: {
    params: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      user: {
        company_name: '',
        first_name: '',
        last_name: '',
        email: '',
        banding: null,
        phone: '',
      },
      bandingOptions: [],
      bandingLoading: false,
      bandingFailed: false,
      bandingFailedMessage:
        'The banding list could not be loaded, so a banding cannot be chosen here. Create the company, then set its banding from the company page.',
      messages: {},
      loading: false,
      successMessage: 'Company created. A welcome email has been sent to the owner.',
      welcomeFailedMessage:
        'Company created, but the welcome email was not sent. Resend it from the company user list.',
      errorMessage: 'Oops, something went wrong! Please check information is correct.',
    };
  },
  mounted() {
    this.getBandings();
  },
  methods: {
    closePanel() {
      this.$emit('closePanel', {});
    },

    getBandings() {
      this.bandingLoading = true;
      companyAPI
        .getAllBandings()
        .then((res) => {
          this.bandingLoading = false;
          const payload = res && res.data ? res.data.banding : null;
          this.bandingOptions = payload ? Object.values(payload) : [];
          this.bandingFailed = this.bandingOptions.length === 0;
        })
        .catch(() => {
          this.bandingLoading = false;
          this.bandingOptions = [];
          this.bandingFailed = true;
        });
    },

    /**
     * A password the admin never sees. It only has to satisfy the API rule
     * min:8|max:50 plus the register regex (lower, upper and one of ?@#$%^&+=!*()- ).
     * The owner replaces it via the welcome email before ever using it.
     */
    generatePassword(length) {
      const size = length || 16;
      const all = LOWER + UPPER + DIGIT + SPECIAL;
      const picked = [
        this.randomChar(LOWER),
        this.randomChar(UPPER),
        this.randomChar(DIGIT),
        this.randomChar(SPECIAL),
      ];
      while (picked.length < size) {
        picked.push(this.randomChar(all));
      }
      // Shuffle so the guaranteed characters are not always in positions 0 to 3.
      for (let i = picked.length - 1; i > 0; i--) {
        const j = this.randomInt(i + 1);
        const tmp = picked[i];
        picked[i] = picked[j];
        picked[j] = tmp;
      }
      return picked.join('');
    },

    randomInt(max) {
      const cryptoObj =
        typeof window !== 'undefined' ? window.crypto || window.msCrypto : null;
      if (cryptoObj && cryptoObj.getRandomValues) {
        const buf = new Uint32Array(1);
        // Reject the tail of the range so the modulo stays uniform.
        const limit = Math.floor(4294967296 / max) * max;
        let value = limit;
        while (value >= limit) {
          cryptoObj.getRandomValues(buf);
          value = buf[0];
        }
        return value % max;
      }
      return Math.floor(Math.random() * max);
    },

    randomChar(pool) {
      return pool.charAt(this.randomInt(pool.length));
    },

    validate() {
      const messages = {};
      const name = (this.user.company_name || '').trim();
      const first = (this.user.first_name || '').trim();
      const last = (this.user.last_name || '').trim();
      const email = (this.user.email || '').trim();

      if (name.length < 2 || name.length > 50) {
        messages.company_name = ['Company name must be between 2 and 50 characters.'];
      }
      if (first.length < 2 || first.length > 50) {
        messages.first_name = ['First name must be between 2 and 50 characters.'];
      }
      if (last.length < 2 || last.length > 50) {
        messages.last_name = ['Last name must be between 2 and 50 characters.'];
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        messages.email = ['Please enter a valid work email address.'];
      }
      if (!this.bandingFailed && !this.user.banding) {
        messages.banding = ['Please choose a banding.'];
      }

      this.messages = messages;
      return Object.keys(messages).length === 0;
    },

    addCompany() {
      // Creating a company is not idempotent: a second submit while the first
      // is in flight would either create a duplicate or 422 on the unique
      // company name and look like the first attempt failed.
      if (this.loading) {
        return;
      }
      if (!this.validate()) {
        this.$snotify.error('Please correct the highlighted fields.');
        return;
      }

      const password = this.generatePassword();
      const payload = {
        company_name: (this.user.company_name || '').trim(),
        first_name: (this.user.first_name || '').trim(),
        last_name: (this.user.last_name || '').trim(),
        email: (this.user.email || '').trim(),
        phone: (this.user.phone || '').trim() || undefined,
        banding: this.user.banding,
        password: password,
        confirm_password: password,
      };

      this.loading = true;
      userAPI
        .createOwner(payload)
        .then((response) => {
          const newUserId = this.readUserId(response);
          if (!newUserId) {
            this.finish(this.welcomeFailedMessage, 'warning');
            return;
          }
          userAPI
            .sendWelcomeEmail(newUserId)
            .then(() => {
              this.finish(this.successMessage, 'success');
            })
            .catch(() => {
              // The company exists. Only the email failed, so do not report a failure.
              this.finish(this.welcomeFailedMessage, 'warning');
            });
        })
        .catch((err) => {
          this.loading = false;
          this.messages = this.apiErrors(err);
          this.$snotify.error(this.errorDetail(err));
          setTimeout(() => {
            this.$gf.removeBackdrop();
          }, 300);
        });
    },

    /**
     * POST /v1/register returns the created user through UserTransformer, wrapped by
     * Fractal's DataArraySerializer, so the hashed id is at response.data.data.id.
     * That is the same hashed id GET /v1/user/send-welcome-email/{id} decodes.
     */
    readUserId(response) {
      if (response && response.data && response.data.data && response.data.data.id) {
        return response.data.data.id;
      }
      return null;
    },

    finish(message, level) {
      this.loading = false;
      EventBus.$emit('USERS_UPDATED', this.params);
      if (level === 'warning') {
        this.$snotify.warning(message);
      } else {
        this.$snotify.success(message);
      }
      this.closePanel();
      setTimeout(() => {
        this.$gf.removeBackdrop();
      }, 300);
    },

    apiErrors(err) {
      const errors =
        err && err.response && err.response.data ? err.response.data.errors : null;
      const messages = {};
      if (errors) {
        Object.keys(errors).forEach((field) => {
          messages[field] = errors[field];
        });
      }
      return messages;
    },

    /**
     * /register answers 422 with per-field messages (duplicate email, duplicate company
     * name, non-business domain). Show the specific one, not a generic failure.
     */
    errorDetail(err) {
      const messages = this.apiErrors(err);
      const fields = Object.keys(messages);
      if (fields.length) {
        const first = messages[fields[0]];
        const text = Array.isArray(first) ? first[0] : first;
        if (text) {
          return fields.length > 1
            ? text + ' (' + fields.length + ' fields need attention)'
            : text;
        }
      }
      if (err && err.response && err.response.data && err.response.data.message) {
        return err.response.data.message;
      }
      return this.errorMessage;
    },
  },
};
</script>
